<template>
  <v-row no-gutters>
    <v-col cols="12">
      <asc-label
        class="asc-plot-y-axis-overrides--label"
        size="sm"
        value="Y Axis Unit Override"
      />
      <v-row class="nowrap" align="end" no-gutters>
        <asc-autocomplete-selector
          v-model="unitIndex"
          class="asc-plot-y-axis-overrides--selector"
          label="Unit"
          :items="units"
          :min-width="105"
          component-type="single"
          no-array
        />
        <asc-text-field-input
          v-model.number="minValue"
          class="asc-plot-y-axis-overrides--input"
          :disabled="unitNotSelected"
          label="Min"
          placeholder="#"
          type="number"
          :hide-details="true"
          @keypress.native="isNumber($event, true)"
        />
        <asc-text-field-input
          v-model.number="maxValue"
          class="asc-plot-y-axis-overrides--input"
          :disabled="unitNotSelected"
          label="Max"
          placeholder="#"
          type="number"
          :hide-details="true"
          @keypress.native="isNumber($event, false)"
        />
        <asc-accent-button
          class="asc-plot-y-axis-overrides--apply-button"
          button-text="APPLY"
          :disabled="isApplyButtonDisabled"
          :on-click="handleApply"
          secondary
          outline-only
        />
        <asc-accent-button
          class="asc-plot-y-axis-overrides--reset-button"
          button-text="RESET"
          :on-click="handleReset"
          icon="mdi-replay"
          icon-only
          secondary
        />
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscAutocompleteSelector from '@/components/molecules/AscAutocompleteSelector'
import AscLabel from '@/components/atoms/AscLabel'
import AscTextFieldInput from '@/components/atoms/AscTextFieldInput'

export default {
  name: 'AscPlotYAxisOverrides',

  components: {
    AscAccentButton,
    AscAutocompleteSelector,
    AscLabel,
    AscTextFieldInput,
  },

  props: {
    overrides: {
      type: Array,
      default: () => [],
    },
    dataOverrides: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      units: this.initUnits(),
      unitIndex: this.initUnitIndex(),
      minValue: this.initMinValue(),
      maxValue: this.initMaxValue(),
    }
  },

  computed: {
    unitNotSelected() {
      return this.unitIndex === null
    },

    isApplyButtonDisabled() {
      const isMinGreaterThanMax = this.minValue > this.maxValue
      const isMinEmpty = this.minValue === null
      const isMaxEmpty = this.maxValue === null
      return isMinGreaterThanMax || isMinEmpty || isMaxEmpty
    },
  },

  watch: {
    overrides: {
      deep: true,
      handler(newOverrides) {
        this.overrides = newOverrides
        this.units = this.initUnits()
        this.updateMinMaxValues()
      },
    },
    unitIndex(newValue) {
      this.unitIndex = newValue
      this.updateMinMaxValues()
    },
  },

  methods: {
    initUnits() {
      const mappedUnits = this.overrides.map((item, index) => {
        return {
          value: index + 1,
          text: item.unit,
          min: item.min,
          max: item.max,
        }
      })

      return mappedUnits
    },

    isNumber(evt, type) {
      const charCode = String.fromCharCode(evt.keyCode)
      if (evt) {
        if (charCode === '-') type ? (this.minValue = '') : (this.maxValue = '')
        if (!/^-?\d*\.?\d*$/.test(charCode)) {
          evt.preventDefault()
        }
        return true
      }
    },

    initUnitIndex() {
      return this.overrides.length > 0 ? 1 : null
    },

    initMinValue() {
      return this.overrides.length > 0 ? this.overrides[0].min : null
    },

    initMaxValue() {
      return this.overrides.length > 0 ? this.overrides[0].max : null
    },

    handleApply() {
      this.units[this.unitIndex - 1].min = this.minValue
      this.units[this.unitIndex - 1].max = this.maxValue
      const unit = this.units[this.unitIndex - 1]
      if (unit) {
        const mappedUnit = {
          min: this.minValue,
          max: this.maxValue,
          unit: unit.text,
          action: 'update',
        }
        this.$emit('changes', mappedUnit)
      }
    },

    handleReset() {
      const unit = this.units[this.unitIndex - 1]
      if (unit) {
        const mappedUnit = {
          unit: unit.text,
          action: 'reset',
        }
        this.$emit('changes', mappedUnit)
      }
    },

    updateMinMaxValues() {
      const item = this.units[this.unitIndex - 1]
      this.minValue = item.min
      this.maxValue = item.max
    },
  },
}
</script>

<style lang="scss">
.asc-plot-y-axis-overrides {
  &--button.asc-accent-button-container.asc-accent-button-container--secondary {
    width: 84px;

    .v-btn.v-btn--depressed.asc-accent-button.asc-accent-button--accent {
      width: 100%;
    }
  }

  &--reset-button {
    margin-left: 12px;
  }

  &--input {
    margin-right: 22px;
    min-height: 44px;
    width: 75px;

    &:nth-child(2) {
      width: 77px;
    }
  }

  &--label {
    &.asc-label--font-md {
      line-height: 18px;
      margin-bottom: 8px;
    }
  }

  &--selector {
    margin-right: 22px;
  }
}
</style>
